import { useMemo, useState } from "react";
import {
  Background,
  BackgroundVariant,
  MiniMap,
  ReactFlow,
  type Edge,
  type ReactFlowInstance,
  useEdgesState,
  useNodesState
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import {
  benchmarkPreviewLimit,
  buildProductionBenchmarkGraph,
  type BenchmarkProductionNode,
  type BenchmarkProductionNodeData
} from "./benchmarkGraph.js";
import { productionNodeTypes } from "./nodes/node-types.js";
import { SeedanceNode } from "./nodes/SeedanceNode.js";

const initialGraph = buildProductionBenchmarkGraph(500, 1000);

const benchmarkNodeTypes = {
  ...productionNodeTypes,
  seedance: SeedanceNode
};

export function CanvasBenchmarkPage() {
  const [nodes, setNodes, onNodesChange] = useNodesState<BenchmarkProductionNode>(initialGraph.nodes);
  const [edges, , onEdgesChange] = useEdgesState(initialGraph.edges);
  const [flow, setFlow] = useState<ReactFlowInstance<BenchmarkProductionNode, Edge> | null>(null);
  const nodeTypes = useMemo(() => benchmarkNodeTypes, []);

  function addNode() {
    setNodes((current) => {
      const id = `node-${current.length}`;
      const position = flow
        ? flow.screenToFlowPosition({
            x: window.innerWidth / 2,
            y: window.innerHeight / 2
          })
        : { x: 0, y: 0 };
      const previewEnabled = current.filter((node) => node.data.previewEnabled).length < benchmarkPreviewLimit;
      return [
        ...current,
        {
          id,
          type: "video",
          width: 190,
          height: 118,
          position,
          data: {
            canvasNode: {
              id,
              kind: "video",
              assetId: `benchmark-asset-${id}`,
              position,
              size: { width: 190, height: 118 },
              collapsed: false
            },
            label: `Video asset · ${current.length + 1}`,
            accent: "#ff6b8a",
            previewEnabled,
            contentUrl: null,
            referenceCounts: { image: 0, video: 0, audio: 0 }
          }
        }
      ];
    });
  }

  return (
    <main className="benchmark-shell">
      <header className="topbar">
        <div className="brand">
          <span className="brand__mark" aria-hidden="true">S</span>
          <div>
            <strong>Seedance Studio V2</strong>
            <small>Infinite canvas · production-node benchmark</small>
          </div>
        </div>
        <div className="canvas-stats" aria-label="Canvas scale">
          <span>Nodes <output data-testid="node-count">{nodes.length}</output></span>
          <span>Edges <output data-testid="edge-count">{edges.length}</output></span>
        </div>
        <nav className="topbar__actions" aria-label="Canvas actions">
          <button className="button button--ghost" data-testid="fit-view" onClick={() => void flow?.fitView({ duration: 350, padding: 0.12 })}>
            Fit canvas
          </button>
          <button className="button button--primary" data-testid="add-node" onClick={addNode}>
            Add node
          </button>
        </nav>
      </header>

      <section className="workspace">
        <aside className="node-palette" aria-label="Node types">
          <span className="node-palette__eyebrow">Nodes</span>
          {[
            ["Text", "#7c6ff2"],
            ["Image asset", "#31a8ff"],
            ["Video asset", "#ff6b8a"],
            ["Audio asset", "#36c98f"],
            ["Seedance", "#f59e4b"],
            ["Composition", "#b06df2"],
            ["Group", "#748094"]
          ].map(([label, color]) => (
            <button className="palette-item" key={label}>
              <span style={{ background: color }} aria-hidden="true" />
              {label}
            </button>
          ))}
          <div className="node-palette__note">
            <strong>500-node stress check</strong>
            <small>Uses production nodes; only a bounded media-preview budget is rendered.</small>
          </div>
        </aside>

        <section className="canvas-panel" data-testid="canvas-root">
          <ReactFlow<BenchmarkProductionNode, Edge>
            nodes={nodes}
            edges={edges}
            nodeTypes={nodeTypes}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onInit={setFlow}
            onlyRenderVisibleElements
            defaultViewport={{ x: 220, y: 130, zoom: 0.78 }}
            minZoom={0.08}
            maxZoom={2}
            panOnScroll
            zoomOnPinch
            selectionOnDrag
            multiSelectionKeyCode="Shift"
            deleteKeyCode={null}
            proOptions={{ hideAttribution: true }}
          >
            <MiniMap
              pannable
              zoomable
              nodeColor={(node) => (node.data as BenchmarkProductionNodeData).accent}
              maskColor="rgba(6, 8, 12, 0.72)"
            />
            <Background variant={BackgroundVariant.Dots} gap={22} size={1.2} color="#323947" />
          </ReactFlow>
          <div className="canvas-hint" aria-hidden="true">Drag nodes · scroll to pan · pinch to zoom · Shift for multi-select</div>
        </section>
      </section>
    </main>
  );
}
